/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function States() {
    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [newRecord, setNewRecord] = useState({ country_id: "", name: "" });
    const [selectedState, setSelectedState] = useState(null);
    const [stateToDelete, setStateToDelete] = useState(null);
    const formRef = useRef(null);

    // Select2 initialization ref
    const select2InitRef = useRef(false);

    // React Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Initialize Select2 when countries are loaded
    useEffect(() => {
        if (countries.length > 0 && !select2InitRef.current) {
            setTimeout(() => {
                try {
                    // Initialize Select2 for all select elements with select2 class
                    $(".select2").each(function() {
                        if (!$(this).hasClass('select2-hidden-accessible')) {
                            $(this).select2({
                                width: '100%'
                            });
                        }
                    });
                    
                    // Handle Select2 change events
                    $('.select2').off('select2:select').on('select2:select', function(e) {
                        const selectElement = e.target;
                        const event = new Event('change', { bubbles: true });
                        selectElement.dispatchEvent(event);
                    });
                    
                    select2InitRef.current = true;
                    console.log("Select2 initialized for States");
                } catch (error) {
                    console.error("Error initializing Select2:", error);
                }
            }, 200);
        }

        // Cleanup function
        return () => {
            try {
                if (select2InitRef.current) {
                    $('.select2').off('select2:select');
                    $('.select2').each(function() {
                        if ($(this).hasClass('select2-hidden-accessible')) {
                            $(this).select2('destroy');
                        }
                    });
                    select2InitRef.current = false;
                }
            } catch (error) {
                console.error("Error cleaning up Select2:", error);
            }
        };
    }, [countries]);

    // Reinitialize Select2 when modals are shown
    useEffect(() => {
        const handleModalShown = (event) => {
            const modalId = event.target.id;
            setTimeout(() => {
                try {
                    // Only reinitialize select2 elements within the shown modal
                    $(`#${modalId} .select2`).each(function() {
                        // Destroy existing select2 if present
                        if ($(this).hasClass('select2-hidden-accessible')) {
                            $(this).select2('destroy');
                        }
                        
                        // Initialize select2
                        $(this).select2({
                            width: '100%',
                            dropdownParent: $(`#${modalId}`)
                        });
                    });
                    
                    // Re-bind change events
                    $(`#${modalId} .select2`).off('select2:select').on('select2:select', function(e) {
                        const selectElement = e.target;
                        const event = new Event('change', { bubbles: true });
                        selectElement.dispatchEvent(event);
                    });
                    
                    console.log(`Select2 reinitialized for modal: ${modalId}`);
                } catch (error) {
                    console.error("Error reinitializing Select2 in modal:", error);
                }
            }, 150);
        };

        // Listen for modal shown events
        $('#add_state, #edit_state').on('shown.bs.modal', handleModalShown);

        return () => {
            $('#add_state, #edit_state').off('shown.bs.modal', handleModalShown);
        };
    }, [countries]);

    
    // Helper function to get country name by ID
    const getCountryName = (countryId) => {
        const country = countries.find(c => c.id == countryId);
        console.log("inside:", country)
        return country ? country.name : 'Unknown';
    };

    // Filter states based on search term
    const filteredStates = states.filter(state =>
        state.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state.country?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getCountryName(state.country_id)?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort filtered states
    const sortedStates = React.useMemo(() => {
        let sortableStates = [...filteredStates];
        if (sortConfig.key) {
            sortableStates.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                
                if (sortConfig.key === 'country_name') {
                    aValue = a.country?.name || getCountryName(a.country_id) || '';
                    bValue = b.country?.name || getCountryName(b.country_id) || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableStates;
    }, [filteredStates, sortConfig]);

    // Calculate pagination
    const totalItems = sortedStates.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentStates = sortedStates.slice(startIndex, endIndex);

    // Handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort icon
    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? '↑' : '↓';
        }
        return '';
    };

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Fetch States
    const fetchStates = async () => {
        try {
            const response = await axiosInstance.get("/state/withcountry");
            setStates(response.data.data);
            console.log("States", response.data.data);
        } catch (error) {
            console.error("Error fetching States:", error);
        }
    };

    // Fetch Countries for dropdown
    const fetchCountries = async () => {
        try {
            const response = await axiosInstance.get("/country/");
            setCountries(response.data.data);
            console.log("Countries", response.data.data);
        } catch (error) {
            console.error("Error fetching Countries:", error);
        }
    };

    // Fetch both states and countries on component mount
    useEffect(() => {
        fetchStates();
        fetchCountries();
    }, []);

    // Open Edit Modal and Set Data
    const handleEditClick = (state) => {
        setSelectedState(state);
        setNewRecord({
            country_id: state.country_id || state.country?.id || "",
            name: state.name || state.name || ""
        });
    };

    // Handle Form Input Change
    const handleChange = (e) => {
        setNewRecord({ ...newRecord, [e.target.name]: e.target.value });
    };

    // Handle Update State
    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedState) return;

        try {
            await axiosInstance.put(`/state/${selectedState.id}`, newRecord);

            setStates(prevStates =>
                prevStates.map(s =>
                    s.id === selectedState.id
                        ? {
                            ...s,
                            name: newRecord.name,
                            country_id: newRecord.country_id || s.country_id
                        }
                        : s
                )
            );

            Swal.fire({
                title: "Updated!",
                text: "State updated successfully!",
                icon: "success"
            }).then(() => {
                $("#edit_state").modal("hide");
            });

            setNewRecord({ country_id: "", name: "" });
            setSelectedState(null);
        } catch (error) {
            console.error("Error updating state:", error);
            Swal.fire("Error", "Failed to update state", "error");
        }
    };

    // Handle Delete Click
    const handleDeleteClick = (state) => {
        setStateToDelete(state);
    };

    // Handle Delete State
    const confirmDelete = async () => {
        if (!stateToDelete) return;

        try {
            await axiosInstance.delete(`/state/${stateToDelete.id}`);

            setStates(prevStates =>
                prevStates.filter(s => s.id !== stateToDelete.id)
            );

            Swal.fire({
                title: "Deleted!",
                text: "State deleted successfully!",
                icon: "success"
            }).then(() => {
                $("#delete_state").modal("hide");
            });

            setStateToDelete(null);
        } catch (error) {
            console.error("Error deleting state:", error);
            Swal.fire("Error", "Failed to delete state", "error");
        }
    };

    // Handle Form Submission (Create)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formRef.current.checkValidity()) {
            formRef.current.classList.add("was-validated");
            return;
        }

        try {
            const payload = {
                ...newRecord,
                created_by: localStorage.getItem("user_id")
            };

            const { data } = await axiosInstance.post("/state", payload);
            console.log("backend:", data.data);
            
            const country = countries.find(c => c.id == data.data.country_id);
            const enrichedRow = {
                ...data.data,
                country_name: country?.name || "",
                country_iso: country?.iso || "",
                country_currency: country?.currency || "",
            };

            setStates(prev => [...prev, enrichedRow]);
            Swal.fire({
                title: "Created!",
                text: "The State record has been created.",
                icon: "success"
            }).then(() => {
                $("#add_state").modal("hide");
            });

            setNewRecord({ country_id: "", name: "" });
            formRef.current.classList.remove("was-validated");
        } catch (error) {
            console.error("Error adding state:", error);
            Swal.fire("Error", "Failed to create state", "error");
        }
    };


    // Pagination component
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    {filteredStates.length !== states.length && ` (filtered from ${states.length} total entries)`}
                </div>
                <div className="d-flex align-items-center">
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> First
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> Previous
                                </button>
                            </li>
                            {pageNumbers.map(number => (
                                <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                    <button 
                                        className="page-link"
                                        onClick={() => setCurrentPage(number)}
                                    >
                                        {number}
                                    </button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next <i className="fa fa-angle-double-right ms-2"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button 
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header ">
                            <h5>States</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <a className="btn btn-filters w-auto popup-toggle" data-bs-toggle="tooltip" data-bs-placement="bottom" data-bs-original-title="Filter"><span className="me-2"><img src="assets/img/icons/filter-icon.svg" alt="filter" /></span>Filter </a>
                                    </li>
                                    <li className="">
                                        <div className="dropdown dropdown-action" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Download">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false"><span><i className="fe fe-download"></i></span></a>
                                            <div className="dropdown-menu dropdown-menu-right">
                                                <ul className="d-block">
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);" download><i className="far fa-file-pdf me-2"></i>PDF</a>
                                                    </li>
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);" download><i className="far fa-file-text me-2"></i>CVS</a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn-filters" href="javascript:void(0);" data-bs-toggle="tooltip" data-bs-placement="bottom" data-bs-original-title="Print"><span><i className="fe fe-printer"></i></span> </a>
                                    </li>
                                    <li>
                                        <a className="btn btn-primary" href="#" data-bs-toggle="modal" data-bs-target="#add_state"><i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add State</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* All Invoice */}
                    <div className="card invoices-tabs-card">
                        <div className="invoices-main-tabs">
                            <div className="row align-items-center">
                                <div className="col-lg-12">
                                    <div className="invoices-tabs">
                                        <ul>
                                            <li><a href="/countries">Country</a></li>
                                            <li><a href="/states" className="active">State</a></li>
                                            <li><a href="/cities">City</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /All Invoice */}

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    {/* Table Controls */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select 
                                                className="form-select form-select-sm" 
                                                style={{width: 'auto'}}
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{width: '200px'}}
                                                placeholder="Search states..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th 
                                                        style={{cursor: 'pointer'}}
                                                        onClick={() => handleSort('name')}
                                                    >
                                                        State Name {getSortIcon('name')}
                                                    </th>
                                                    <th 
                                                        style={{cursor: 'pointer'}}
                                                        onClick={() => handleSort('country_name')}
                                                    >
                                                        Country {getSortIcon('country_name')}
                                                    </th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentStates.length > 0 ? (
                                                    currentStates.map((state, index) => {
                                                        return (
                                                            <tr key={state.id}>
                                                                <td>{startIndex + index + 1}</td>
                                                                <td>{state.name || state.name}</td>
                                                                <td>{state.country?.name || getCountryName(state.country_id)}</td>
                                                                <td className="d-flex align-items-center">
                                                                    <button
                                                                        className="btn btn-greys bg-success-light me-2"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#edit_state"
                                                                        onClick={() => handleEditClick(state)}
                                                                    >
                                                                        <i className="fa fa-edit me-1"></i>Edit
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-greys bg-danger-light me-2"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#delete_state"
                                                                        onClick={() => handleDeleteClick(state)}
                                                                    >
                                                                        <i className="fa fa-trash-alt me-1"></i> Delete
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center">
                                                            {searchTerm ? "No matching states found" : "No states available in table"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Table */}
                </div>
                <Footer />
            </div>
            {/* /Page Wrapper */}

            {/* Add State Modal */}
            <div className="modal custom-modal fade" id="add_state" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0">Add State</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                            </button>
                        </div>
                        <form ref={formRef} onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Country<span className="text-danger">*</span></label>
                                            <select
                                                className="select2"
                                                name="country_id"
                                                value={newRecord.country_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Country</option>
                                                {countries.map((country) => (
                                                    <option key={country.id} value={country.id}>
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>State Name<span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={newRecord.name}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter State Name"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">Add State</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {/* /Add State Modal */}

            {/* Edit State Modal */}
            <div className="modal custom-modal fade" id="edit_state" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0">Edit State</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Country<span className="text-danger">*</span></label>
                                        <select
                                            className="select2"
                                            name="country_id"
                                            value={newRecord.country_id}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Country</option>
                                            {countries.map((country) => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>State Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={newRecord.name}
                                            onChange={handleChange}
                                            placeholder="Enter State Name"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                            <button type="button" onClick={handleUpdate} className="btn btn-primary paid-continue-btn">Update</button>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Edit State Modal */}

            {/* Delete State Modal */}
            <div className="modal custom-modal fade" id="delete_state" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className="form-header">
                                <h3>Delete State</h3>
                                <p>Are you sure want to delete "{stateToDelete?.name || stateToDelete?.name}"?</p>
                            </div>
                            <div className="modal-btn delete-action">
                                <div className="row">
                                    <div className="col-6">
                                        <button type="button" className="w-100 btn btn-primary paid-continue-btn" onClick={confirmDelete}>Delete</button>
                                    </div>
                                    <div className="col-6">
                                        <button type="button" data-bs-dismiss="modal" className="w-100 btn btn-primary paid-cancel-btn">Cancel</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Delete State Modal */}
        </div>
    )
}

export default States